import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Badge,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Add, Check, Close, ContentCopy, FactCheckOutlined, HelpOutline, Search } from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { Product } from "../../types";
import { answerDeskService, FaqEntry, FaqMatchMap, FaqTopic } from "../../services/answerDeskService";
import { AnswerCard } from "./components/AnswerCard";
import { AnswerImagesHandle } from "./components/AnswerImages";
import { AnswerEditorDialog, EditorPrefill } from "./components/AnswerEditorDialog";
import { AskManagerDialog } from "./components/AskManagerDialog";
import { FlagDialog } from "./components/FlagDialog";
import { ProductContextCard } from "./components/ProductContextCard";
import { ProductPicker } from "./components/ProductPicker";
import { useAnswerDeskData } from "./useAnswerDeskData";
import { resolveFillProducts, useTargetProducts } from "./targetProducts";
import {
    apiErrorMessage,
    buildSearchIndex,
    copiesHighlightedAnswer,
    copyText,
    fillAnswer,
    imageShortcutIndex,
    imageShortcutProblem,
    needsProduct,
    productChoiceNeeded,
    productListText,
    searchEntries,
    segmentsToText,
    TOPICS,
} from "./answerDeskUtils";

type TopicFilter = FaqTopic | "ALL";

interface Section {
    key: string;
    title: string;
    entries: FaqEntry[];
    note?: string;
    /** Shown when the section is empty (e.g. a product with no own answers). */
    emptyText?: string;
}

const BROWSE_LIMIT = 60;

const byUsage = (a: FaqEntry, b: FaqEntry) =>
    Number(b.is_pinned) - Number(a.is_pinned) || b.copy_count - a.copy_count || a.question.localeCompare(b.question);

export const AnswerDesk: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canPublish = user?.role === "OWNER" || user?.role === "MANAGER";
    const { entries, deliveryAreas, loading, error, setError, upsert, removeLocal, bumpCopyCount, reload } = useAnswerDeskData();

    const [query, setQuery] = useState("");
    const [products, setProducts] = useState<Product[]>([]);
    const [matchMap, setMatchMap] = useState<FaqMatchMap>({});
    const [topic, setTopic] = useState<TopicFilter>("ALL");
    const [focusIndex, setFocusIndex] = useState(0);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const [showAllBrowse, setShowAllBrowse] = useState(false);
    const [editor, setEditor] = useState<{ entry: FaqEntry | null; prefill?: EditorPrefill } | null>(null);
    const [askOpen, setAskOpen] = useState(false);
    const [flagEntry, setFlagEntry] = useState<FaqEntry | null>(null);
    const [toast, setToast] = useState<{ message: string; severity: "success" | "error" } | null>(null);
    const [addingStarter, setAddingStarter] = useState(false);

    const searchRef = useRef<HTMLInputElement | null>(null);
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
    const imageRefs = useRef<(AnswerImagesHandle | null)[]>([]);
    const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const published = useMemo(() => entries.filter((e) => e.status === "PUBLISHED"), [entries]);
    const searchIndex = useMemo(() => buildSearchIndex(published), [published]);
    const quickReplies = useMemo(
        () => published.filter((e) => e.topic === "QUICK_REPLY").sort(byUsage),
        [published],
    );

    // ── Product context: which answers apply to which product ──────────────
    const productKey = products.map((p) => p.product_id).join(",");
    const publishedKey = useMemo(
        () => published.map((e) => `${e.faq_entry_id}:${e.update_time}`).join("|"),
        [published],
    );
    useEffect(() => {
        if (!productKey) {
            setMatchMap({});
            return;
        }
        let cancelled = false;
        answerDeskService
            .matchProducts(productKey.split(","))
            .then((m) => {
                if (!cancelled) setMatchMap(m);
            })
            .catch(() => {
                if (!cancelled) setMatchMap({});
            });
        return () => {
            cancelled = true;
        };
    }, [productKey, publishedKey]);

    const matchIndex = useMemo(() => {
        const index = new Map<string, Map<string, number>>();
        Object.entries(matchMap).forEach(([pid, items]) => {
            index.set(pid, new Map(items.map((i) => [i.faq_entry_id, i.level])));
        });
        return index;
    }, [matchMap]);

    // ── What to show ───────────────────────────────────────────────────────
    const trimmedQuery = query.trim();
    const byId = useMemo(() => new Map(published.map((e) => [e.faq_entry_id, e])), [published]);

    const { baseEntries, buildSections } = useMemo(() => {
        const notQuick = (e: FaqEntry) => e.topic !== "QUICK_REPLY";

        if (trimmedQuery) {
            const hits = searchEntries(searchIndex, trimmedQuery);
            const boost = (e: FaqEntry) =>
                (products.some((p) => matchIndex.get(p.product_id)?.has(e.faq_entry_id)) ? 6 : 0) +
                (e.is_pinned ? 2 : 0) +
                Math.log10(e.copy_count + 1);
            const ranked = hits
                .sort((a, b) => b.score + boost(b.entry) - (a.score + boost(a.entry)))
                .map((h) => h.entry);
            const isPartial = hits.length > 0 && hits[0].partial;
            return {
                baseEntries: ranked,
                buildSections: (filter: (e: FaqEntry) => boolean): Section[] => [
                    {
                        key: "search",
                        title: isPartial ? "Close matches" : "Results",
                        note: isPartial ? "Nothing matched every word — these match some of them." : undefined,
                        entries: ranked.filter(filter),
                    },
                ],
            };
        }

        const generals = published.filter((e) => e.is_general).sort(byUsage);

        if (products.length > 0) {
            const levelOf = (pid: string, id: string) => matchIndex.get(pid)?.get(id) ?? -1;
            const matchedIds = new Set<string>();
            products.forEach((p) => matchIndex.get(p.product_id)?.forEach((_, id) => matchedIds.add(id)));
            const matched = Array.from(matchedIds)
                .map((id) => byId.get(id))
                .filter((e): e is FaqEntry => !!e);
            const bestLevel = (e: FaqEntry) => Math.max(...products.map((p) => levelOf(p.product_id, e.faq_entry_id)));
            const sortMatched = (list: FaqEntry[]) => [...list].sort((a, b) => bestLevel(b) - bestLevel(a) || byUsage(a, b));

            const base = [...matched, ...generals];
            if (products.length === 1) {
                return {
                    baseEntries: base,
                    buildSections: (filter: (e: FaqEntry) => boolean): Section[] => [
                        {
                            key: "product",
                            title: "For this product",
                            entries: sortMatched(matched).filter(filter),
                            emptyText: "No answers just for this product yet.",
                        },
                        { key: "general", title: "General answers", entries: generals.filter((e) => filter(e) && notQuick(e)) },
                    ],
                };
            }
            const sharedSet = new Set(
                matched.filter((e) => products.every((p) => levelOf(p.product_id, e.faq_entry_id) >= 0)).map((e) => e.faq_entry_id),
            );
            return {
                baseEntries: base,
                buildSections: (filter: (e: FaqEntry) => boolean): Section[] => [
                    {
                        key: "shared",
                        title: `For all ${products.length} products`,
                        entries: sortMatched(matched.filter((e) => sharedSet.has(e.faq_entry_id))).filter(filter),
                        emptyText: "Nothing written for all of them together.",
                    },
                    ...products.map((p) => ({
                        key: `only-${p.product_id}`,
                        title: `Only ${p.name}`,
                        entries: sortMatched(
                            matched.filter((e) => !sharedSet.has(e.faq_entry_id) && levelOf(p.product_id, e.faq_entry_id) >= 0),
                        ).filter(filter),
                        emptyText: "No answers just for this product yet.",
                    })),
                    { key: "general", title: "General answers", entries: generals.filter((e) => filter(e) && notQuick(e)) },
                ],
            };
        }

        const all = [...published].sort(byUsage);
        return {
            baseEntries: all,
            buildSections: (filter: (e: FaqEntry) => boolean): Section[] => {
                const list = all.filter((e) => filter(e) && (topic === "QUICK_REPLY" || notQuick(e)));
                return [
                    {
                        key: "browse",
                        title: topic === "ALL" ? "Most used answers" : TOPICS.find((t) => t.value === topic)?.label ?? "Answers",
                        entries: showAllBrowse ? list : list.slice(0, BROWSE_LIMIT),
                        note:
                            !showAllBrowse && list.length > BROWSE_LIMIT
                                ? `Showing ${BROWSE_LIMIT} of ${list.length}. Search or pick a product to narrow it down.`
                                : undefined,
                    },
                ];
            },
        };
    }, [trimmedQuery, searchIndex, products, matchIndex, published, byId, topic, showAllBrowse]);

    const topicCounts = useMemo(() => {
        const counts = new Map<TopicFilter, number>();
        const unique = new Map(baseEntries.map((e) => [e.faq_entry_id, e]));
        counts.set("ALL", Array.from(unique.values()).filter((e) => trimmedQuery || e.topic !== "QUICK_REPLY").length);
        unique.forEach((e) => counts.set(e.topic, (counts.get(e.topic) ?? 0) + 1));
        return counts;
    }, [baseEntries, trimmedQuery]);

    // Fall back to "All" when the chosen topic has nothing in this view.
    useEffect(() => {
        if (topic !== "ALL" && !topicCounts.get(topic)) setTopic("ALL");
    }, [topic, topicCounts]);

    const sections = useMemo(
        () => buildSections((e) => topic === "ALL" || e.topic === topic),
        [buildSections, topic],
    );
    const ordered = useMemo(() => {
        const seen = new Set<string>();
        const list: FaqEntry[] = [];
        sections.forEach((s) =>
            s.entries.forEach((e) => {
                if (!seen.has(e.faq_entry_id)) {
                    seen.add(e.faq_entry_id);
                    list.push(e);
                }
            }),
        );
        return list;
    }, [sections]);
    const shownCount = ordered.length;

    // Products an answer's {price}/{stock}/... are filled from — see
    // resolveFillProducts: chat products it's for, else its own products.
    const targetProducts = useTargetProducts(ordered);
    const fillWithFor = useCallback(
        (entry: FaqEntry): Product[] => resolveFillProducts(entry, products, matchIndex, targetProducts),
        [products, matchIndex, targetProducts],
    );

    // Mirrors focusIndex synchronously so fast key presses (↓ then Enter
    // before React re-renders) act on the card that's actually highlighted.
    const focusRef = useRef(0);
    const moveFocus = (index: number) => {
        focusRef.current = index;
        setFocusIndex(index);
    };
    useEffect(() => {
        focusRef.current = 0;
        setFocusIndex(0);
    }, [trimmedQuery, productKey, topic]);

    // ── Copy ───────────────────────────────────────────────────────────────
    const flashCopied = (key: string) => {
        setCopiedKey(key);
        if (copiedTimer.current) clearTimeout(copiedTimer.current);
        copiedTimer.current = setTimeout(() => setCopiedKey(null), 2000);
    };
    useEffect(() => () => {
        if (copiedTimer.current) clearTimeout(copiedTimer.current);
    }, []);

    const copyRaw = async (key: string, text: string) => {
        if (await copyText(text)) flashCopied(key);
        else setToast({ message: "Couldn't copy — select the text and copy it manually.", severity: "error" });
    };

    const copyEntry = useCallback(
        async (entry: FaqEntry, product: Product | null) => {
            const text = segmentsToText(fillAnswer(entry.answer ?? "", product, deliveryAreas));
            if (await copyText(text)) {
                flashCopied(entry.faq_entry_id);
                answerDeskService.recordCopy(entry.faq_entry_id);
                bumpCopyCount(entry.faq_entry_id);
            } else {
                setToast({ message: "Couldn't copy — select the text and copy it manually.", severity: "error" });
            }
        },
        [deliveryAreas, bumpCopyCount],
    );

    // ── Keyboard: / focuses search, ↑/↓ move, Enter or ⌘C copies, Esc clears ──
    // One listener for the page's lifetime; it reads the latest values from
    // a ref instead of being re-attached (and briefly stale) on every render.
    const dialogOpen = !!editor || askOpen || !!flagEntry;
    const keyState = useRef({ dialogOpen, ordered, fillWithFor, copyEntry });
    keyState.current = { dialogOpen, ordered, fillWithFor, copyEntry };
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const { dialogOpen, ordered, fillWithFor, copyEntry } = keyState.current;
            // The highlighted answer and, if it can't be copied straight away
            // (it needs a product picked), why not.
            const highlighted = () => {
                const entry = ordered[Math.min(focusRef.current, ordered.length - 1)];
                const candidates = entry ? fillWithFor(entry) : [];
                return { entry, candidates, blocked: entry ? productChoiceNeeded(entry, candidates) : null };
            };
            // ⌘C copies the highlighted answer — unless some text is selected
            // (see copiesHighlightedAnswer).
            if (!dialogOpen && ordered.length > 0 && copiesHighlightedAnswer(e, searchRef.current)) {
                e.preventDefault();
                const { entry, candidates, blocked } = highlighted();
                if (blocked) setToast({ message: blocked, severity: "error" });
                else if (entry) copyEntry(entry, needsProduct(entry.answer) ? candidates[0] : null);
                return;
            }
            // ⌥C copies its first image, ⌘⌥2 the second, … — straight from
            // this handler, which Safari needs for an image clipboard write.
            const imageIndex = !dialogOpen && ordered.length > 0 ? imageShortcutIndex(e, searchRef.current) : null;
            if (imageIndex !== null) {
                e.preventDefault();
                const focus = Math.min(focusRef.current, ordered.length - 1);
                const problem = imageShortcutProblem(ordered[focus], imageIndex);
                if (problem) setToast({ message: problem, severity: "error" });
                else imageRefs.current[focus]?.copy(imageIndex);
                return;
            }
            if (dialogOpen || e.altKey || e.ctrlKey || e.metaKey) return;
            const target = e.target as HTMLElement | null;
            const inSearch = target === searchRef.current;
            const typing =
                !!target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
            if (e.key === "/" && !typing) {
                e.preventDefault();
                searchRef.current?.focus();
                return;
            }
            if (typing && !inSearch) return;
            if (e.key === "Escape" && inSearch) {
                setQuery("");
                return;
            }
            if (ordered.length === 0) return;
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                const next = Math.min(
                    ordered.length - 1,
                    Math.max(0, focusRef.current + (e.key === "ArrowDown" ? 1 : -1)),
                );
                focusRef.current = next;
                setFocusIndex(next);
                cardRefs.current[next]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
                return;
            }
            if (e.key === "Enter") {
                const { entry, candidates, blocked } = highlighted();
                if (!entry) return;
                if (blocked) {
                    // A focused button keeps its own Enter (the card's Copy opens
                    // the product menu); otherwise say why nothing was copied.
                    if (target?.tagName !== "BUTTON") setToast({ message: blocked, severity: "error" });
                    return;
                }
                e.preventDefault();
                copyEntry(entry, needsProduct(entry.answer) ? candidates[0] : null);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // ── Waiting work (shown on the Review button) ──────────────────────────
    const waiting = useMemo(() => {
        if (canPublish) {
            return entries.filter(
                (e) =>
                    e.status === "REQUESTED" ||
                    e.status === "DRAFT" ||
                    (e.status === "PUBLISHED" && (e.flagged_at || (!e.is_general && e.targets.length === 0))),
            ).length;
        }
        return entries.filter((e) => e.is_mine && (e.status === "REQUESTED" || e.status === "DRAFT")).length;
    }, [entries, canPublish]);

    const addStarterPack = async () => {
        setAddingStarter(true);
        try {
            await answerDeskService.addStarterPack();
            await reload();
            setToast({ message: "Starter answers added as drafts — review and publish them in the Review queue.", severity: "success" });
        } catch (err) {
            setToast({ message: apiErrorMessage(err, "Couldn't add the starter answers."), severity: "error" });
        } finally {
            setAddingStarter(false);
        }
    };

    const openSuggest = (prefill?: EditorPrefill) => setEditor({ entry: null, prefill });

    // ── Render ─────────────────────────────────────────────────────────────
    const quickRepliesPanel = quickReplies.length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 3, px: 2, py: 1 }}>
            <Typography variant="overline" color="text.secondary">
                Quick replies
            </Typography>
            {quickReplies.map((q) => (
                <Stack key={q.faq_entry_id} direction="row" alignItems="center" spacing={1} sx={{ minHeight: 44, borderTop: 1, borderColor: "divider" }}>
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={q.answer ?? ""}>
                        {q.question}
                    </Typography>
                    <Button
                        size="small"
                        color={copiedKey === q.faq_entry_id ? "success" : "primary"}
                        startIcon={copiedKey === q.faq_entry_id ? <Check /> : <ContentCopy fontSize="small" />}
                        onClick={() => copyEntry(q, products[0] ?? null)}
                        aria-label={`Copy the “${q.question}” reply`}
                    >
                        {copiedKey === q.faq_entry_id ? "Copied" : "Copy"}
                    </Button>
                </Stack>
            ))}
        </Paper>
    );

    return (
        <Box sx={{ maxWidth: 1240 }}>
            <Stack direction="row" alignItems="flex-end" flexWrap="wrap" useFlexGap spacing={2} sx={{ mb: 2.5 }}>
                <Box sx={{ flex: "1 1 320px" }}>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }}>
                        Answer Desk
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Find a ready reply for a customer chat. Prices, stock and links fill in live.
                    </Typography>
                </Box>
                <Badge badgeContent={waiting} color="error" invisible={waiting === 0}>
                    <Button variant="outlined" startIcon={<FactCheckOutlined />} onClick={() => navigate("/answers/review")}>
                        {canPublish ? "Review queue" : "My questions"}
                    </Button>
                </Badge>
                <Button variant="contained" disableElevation startIcon={<Add />} onClick={() => openSuggest()}>
                    {canPublish ? "New answer" : "Suggest an answer"}
                </Button>
            </Stack>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5} sx={{ mb: 2.5 }}>
                <TextField
                    inputRef={searchRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search a question — Bangla, English or Banglish (“dam koto”)"
                    autoFocus
                    sx={{
                        flex: "1 1 420px",
                        "& .MuiOutlinedInput-root": { bgcolor: "background.paper", minHeight: 56 },
                    }}
                    inputProps={{ "aria-label": "Search questions", style: { fontSize: 16 } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="primary" />
                            </InputAdornment>
                        ),
                        endAdornment: query ? (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setQuery("")} aria-label="Clear search" edge="end">
                                    <Close />
                                </IconButton>
                            </InputAdornment>
                        ) : (
                            <InputAdornment position="end" sx={{ display: { xs: "none", md: "flex" } }}>
                                <Box component="kbd" sx={{ fontSize: 12, color: "text.secondary", border: 1, borderColor: "divider", borderRadius: 0.5, px: 0.75 }}>
                                    /
                                </Box>
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ flex: "1 1 360px", minWidth: 0, "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}>
                    <ProductPicker value={products} onChange={setProducts} />
                </Box>
            </Stack>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 2.5 }}>
                    {(products.length > 0 || quickReplies.length > 0) && (
                        <Stack spacing={1.5} sx={{ flex: "1 1 300px", maxWidth: { md: 380 }, minWidth: 0 }}>
                            {products.map((p) => (
                                <ProductContextCard
                                    key={p.product_id}
                                    product={p}
                                    compact={products.length > 1}
                                    copiedKey={copiedKey}
                                    onCopy={copyRaw}
                                    onRemove={(r) => setProducts((prev) => prev.filter((x) => x.product_id !== r.product_id))}
                                />
                            ))}
                            {products.length > 1 && (
                                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        disableElevation
                                        color={copiedKey === "list" ? "success" : "primary"}
                                        startIcon={copiedKey === "list" ? <Check /> : <ContentCopy fontSize="small" />}
                                        onClick={() => copyRaw("list", productListText(products))}
                                    >
                                        {copiedKey === "list" ? "Copied" : `Copy all ${products.length} as a list`}
                                    </Button>
                                </Paper>
                            )}
                            <Box sx={{ display: { xs: "none", md: "block" } }}>{quickRepliesPanel}</Box>
                        </Stack>
                    )}

                    <Stack spacing={1.75} sx={{ flex: "999 1 520px", minWidth: 0 }} component="section" aria-label="Answers">
                        {/* One swipeable row on phones, wrapping from tablet up. */}
                        <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            sx={{
                                flexWrap: { xs: "nowrap", sm: "wrap" },
                                overflowX: { xs: "auto", sm: "visible" },
                                mx: { xs: -2, sm: 0 },
                                px: { xs: 2, sm: 0 },
                                pb: { xs: 0.5, sm: 0 },
                                scrollbarWidth: "none",
                                "&::-webkit-scrollbar": { display: "none" },
                                "& > *": { flexShrink: 0 },
                            }}
                        >
                            {[{ value: "ALL" as TopicFilter, label: "All" }, ...TOPICS]
                                .filter((t) => t.value === "ALL" || topicCounts.get(t.value as TopicFilter))
                                .map((t) => (
                                    <Chip
                                        key={t.value}
                                        label={`${t.label} ${topicCounts.get(t.value as TopicFilter) ?? 0}`}
                                        onClick={() => setTopic(t.value as TopicFilter)}
                                        color={topic === t.value ? "primary" : "default"}
                                        variant={topic === t.value ? "filled" : "outlined"}
                                        aria-pressed={topic === t.value}
                                        sx={{ fontWeight: 500, bgcolor: topic === t.value ? undefined : "background.paper" }}
                                    />
                                ))}
                        </Stack>

                        {published.length === 0 ? (
                            <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
                                <HelpOutline color="primary" sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    No answers yet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 460, mx: "auto" }}>
                                    {canPublish && waiting > 0
                                        ? `${waiting} ${waiting === 1 ? "answer is" : "answers are"} waiting in the Review queue. Publish them to make them copyable here.`
                                        : canPublish
                                          ? "Write the first answer, or start from a set of common ones (delivery, payment, returns, quick replies) saved as drafts for you to check."
                                          : "Ask a manager when a customer asks something — answered questions show up here for everyone."}
                                </Typography>
                                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                                    {canPublish && entries.length === 0 && (
                                        <Button variant="outlined" onClick={addStarterPack} disabled={addingStarter}>
                                            Add starter answers
                                        </Button>
                                    )}
                                    {canPublish && waiting > 0 && (
                                        <Button variant="outlined" onClick={() => navigate("/answers/review")}>
                                            Open Review queue
                                        </Button>
                                    )}
                                    <Button variant="contained" disableElevation onClick={() => (canPublish ? openSuggest() : setAskOpen(true))}>
                                        {canPublish ? "New answer" : "Ask a manager"}
                                    </Button>
                                </Stack>
                            </Paper>
                        ) : trimmedQuery && shownCount === 0 ? (
                            <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
                                <HelpOutline color="primary" sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    No answer yet for “{trimmedQuery}”
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Ask a manager — once it's answered, it'll be here for everyone next time.
                                </Typography>
                                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                                    <Button variant="contained" disableElevation onClick={() => setAskOpen(true)}>
                                        Ask a manager
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            openSuggest({ question: trimmedQuery, alt_phrasings: [trimmedQuery], products })
                                        }
                                    >
                                        Suggest an answer
                                    </Button>
                                </Stack>
                            </Paper>
                        ) : (
                            sections.map((section) => {
                                const hideEmpty = !section.emptyText && section.entries.length === 0;
                                if (hideEmpty) return null;
                                const showHeading = sections.length > 1 || section.key === "search" || section.key === "browse";
                                return (
                                    <Stack key={section.key} spacing={1.25}>
                                        {showHeading && (
                                            <Typography variant="overline" color="text.secondary" component="h2" sx={{ lineHeight: 1.8 }}>
                                                {section.title}
                                                {section.entries.length > 0 ? ` · ${section.entries.length}` : ""}
                                            </Typography>
                                        )}
                                        {section.note && (
                                            <Typography variant="body2" color="text.secondary">
                                                {section.note}
                                                {section.key === "browse" && !showAllBrowse && (
                                                    <Button size="small" onClick={() => setShowAllBrowse(true)} sx={{ ml: 1 }}>
                                                        Show all
                                                    </Button>
                                                )}
                                            </Typography>
                                        )}
                                        {section.entries.length === 0 && section.emptyText ? (
                                            <Paper
                                                variant="outlined"
                                                sx={{ p: 2, borderRadius: 3, borderStyle: "dashed", display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
                                            >
                                                <Typography variant="body2" color="text.secondary" sx={{ flex: "1 1 240px" }}>
                                                    {section.emptyText}
                                                </Typography>
                                                <Button variant="outlined" size="small" onClick={() => setAskOpen(true)}>
                                                    Ask a manager
                                                </Button>
                                            </Paper>
                                        ) : (
                                            section.entries.map((entry) => {
                                                const idx = ordered.indexOf(entry);
                                                return (
                                                    <AnswerCard
                                                        key={`${section.key}-${entry.faq_entry_id}`}
                                                        ref={(el) => {
                                                            cardRefs.current[idx] = el;
                                                        }}
                                                        entry={entry}
                                                        fillWith={fillWithFor(entry)}
                                                        deliveryAreas={deliveryAreas}
                                                        focused={idx === focusIndex}
                                                        copied={copiedKey === entry.faq_entry_id}
                                                        canEdit={canPublish}
                                                        onCopy={copyEntry}
                                                        onFlag={setFlagEntry}
                                                        onEdit={(e) => setEditor({ entry: e })}
                                                        onFocus={() => moveFocus(idx)}
                                                        imagesRef={(handle) => {
                                                            imageRefs.current[idx] = handle;
                                                        }}
                                                    />
                                                );
                                            })
                                        )}
                                    </Stack>
                                );
                            })
                        )}

                        {published.length > 0 && !(trimmedQuery && shownCount === 0) && (
                            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                                Can't find it?{" "}
                                <Button size="small" onClick={() => setAskOpen(true)}>
                                    Ask a manager
                                </Button>
                            </Typography>
                        )}
                    </Stack>

                    <Box sx={{ display: { xs: "block", md: "none" }, flex: "1 1 100%" }}>{quickRepliesPanel}</Box>
                </Box>
            )}

            <AnswerEditorDialog
                open={!!editor}
                entry={editor?.entry ?? null}
                prefill={editor?.prefill}
                previewProducts={products}
                canPublish={canPublish}
                deliveryAreas={deliveryAreas}
                onClose={() => setEditor(null)}
                onSaved={(saved, message) => {
                    const editing = editor?.entry;
                    setEditor(null);
                    if (saved && saved.status !== "ARCHIVED") upsert(saved);
                    else if (editing) removeLocal(editing.faq_entry_id);
                    setToast({ message, severity: "success" });
                }}
            />
            <AskManagerDialog
                open={askOpen}
                searchText={trimmedQuery}
                products={products}
                onClose={() => setAskOpen(false)}
                onSent={(entry) => {
                    setAskOpen(false);
                    upsert(entry);
                    setToast({ message: "Sent to the review queue", severity: "success" });
                }}
                onSuggestInstead={(question, words, picked) => {
                    setAskOpen(false);
                    openSuggest({ question, alt_phrasings: words && words !== question ? [words] : [], products: picked });
                }}
            />
            <FlagDialog
                entry={flagEntry}
                onClose={() => setFlagEntry(null)}
                onFlagged={(entry) => {
                    setFlagEntry(null);
                    upsert(entry);
                    setToast({ message: "Flagged — a manager will check it", severity: "success" });
                }}
            />
            <Snackbar
                open={!!toast}
                autoHideDuration={4000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setToast(null)} severity={toast?.severity ?? "success"} variant="filled" sx={{ width: "100%" }}>
                    {toast?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AnswerDesk;
